"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.doNooAddressesMatch = exports.isAddressComplete = exports.areAddressesEqual = void 0;
const residenceType_1 = require("./residenceType");
// Kind of a hack so we get a type error if we add a new key to ComparableAddress and forget to
// compare it — a silently-unchecked part means two different addresses compare equal.
const comparedAddressPartsObj = {
    addressUnit: null,
    addressStreet: null,
    addressCity: null,
    addressState: null,
    addressPostalCode: null,
};
const comparedAddressParts = Object.keys(comparedAddressPartsObj);
// null, undefined and '' all mean "no value" — an absent unit must equal a blank unit, otherwise
// the same address compares unequal purely because one caller defaults to '' and another to undefined.
const normalizeAddressPart = (value) => (value ?? '').trim().toUpperCase();
const normalizeAddress = (address) => ({
    // Coalesce the two unit spellings here so no caller can silently drop the unit and make a duplex
    // owner's two halves compare as one address.
    addressUnit: normalizeAddressPart(address.addressUnit ?? address.secondaryAddressUnit),
    addressStreet: normalizeAddressPart(address.addressStreet),
    addressCity: normalizeAddressPart(address.addressCity),
    addressState: normalizeAddressPart(address.addressState),
    addressPostalCode: normalizeAddressPart(address.addressPostalCode),
});
/**
 * Case- and whitespace-insensitive equality across every part of an address. Shared by origination
 * FE, partner FE and backend so the "you entered the same address twice" check can't drift between
 * them.
 */
const areAddressesEqual = (first, second, options) => {
    const normalizedFirst = normalizeAddress(first);
    const normalizedSecond = normalizeAddress(second);
    return comparedAddressParts.every((part) => {
        if (part === 'addressStreet' && options?.doStreetsMatch) {
            return options.doStreetsMatch(normalizedFirst[part], normalizedSecond[part]);
        }
        return normalizedFirst[part] === normalizedSecond[part];
    });
};
exports.areAddressesEqual = areAddressesEqual;
/**
 * Whether an address has enough parts to be worth comparing. Blank strings arrive from forms that
 * default their fields, and two all-blank addresses would otherwise compare equal.
 */
const isAddressComplete = (address) => {
    const normalized = normalizeAddress(address);
    return Boolean(normalized.addressStreet && normalized.addressCity && normalized.addressState && normalized.addressPostalCode);
};
exports.isAddressComplete = isAddressComplete;
/**
 * True when a non-owner-occupied applicant gave the same address for the collateral property and for
 * their own primary residence — the LA 4723926 shape, where the borrower stated "investment property"
 * but lives there. This is the single definition of that rule: origination FE, the partner flow FE
 * and the backend all call it, so the check can't drift between the channels.
 *
 * Returns false when either address is incomplete or absent, because callers legitimately omit the
 * primary residence (the MLO lead form defaults it to the property address) and rejecting those would
 * block applications that work today.
 */
const doNooAddressesMatch = ({ residenceType, propertyAddress, personalAddress, doStreetsMatch, }) => {
    if (!residenceType || !residenceType_1.NOO_RESIDENCE_TYPES.includes(residenceType)) {
        return false;
    }
    if (!propertyAddress || !personalAddress || !(0, exports.isAddressComplete)(propertyAddress) || !(0, exports.isAddressComplete)(personalAddress)) {
        return false;
    }
    return (0, exports.areAddressesEqual)(propertyAddress, personalAddress, { doStreetsMatch });
};
exports.doNooAddressesMatch = doNooAddressesMatch;
